import { useEffect, useState } from 'react';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { imageFormats } from '../utils/constant';

const PreviewFileModal = ({
  openModal,
  setOpenModal,
  fileLink,
  attachments,
}) => {
  const [fileType, setFileType] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSheet, setActiveSheet] = useState(0);
  const [sheets, setSheets] = useState([]);
  const [wordStyles, setWordStyles] = useState('');
  const [previewMode, setPreviewMode] = useState(''); // 'google', 'microsoft', or 'local'

  useEffect(() => {
    if (fileLink) {
      const matchedAttachment = attachments.find(
        (attachment) => attachment.attachmentUrl === fileLink
      );
      if (matchedAttachment) {
        setFileName(matchedAttachment.name);
        const fileExtension = matchedAttachment.name.split('.').pop().toLowerCase();
        setFileType(fileExtension);
        
        // Load content for Word/Excel files
        if (['doc', 'docx', 'xls', 'xlsx'].includes(fileExtension)) {
          loadFileContent(fileLink, fileExtension);
        }
      }
    }
  }, [fileLink, attachments]);

  const loadFileContent = async (url, extension) => {
    setLoading(true);
    setError('');
    setPreviewContent('');
    setSheets([]);
    setActiveSheet(0);

    // For Word and Excel files, try appropriate viewers first
    if (['doc', 'docx', 'xls', 'xlsx'].includes(extension)) {
      let viewerUrl = '';
      
      // Use Microsoft Office Online Viewer for Excel files
      if (['xls', 'xlsx'].includes(extension)) {
        viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      } 
      // Use Google Drive Viewer for Word files
      else if (['doc', 'docx'].includes(extension)) {
        viewerUrl = `https://drive.google.com/viewerng/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      }
      
      // Test if the file is accessible to the viewer
      try {
        const testResponse = await fetch(url, { method: 'HEAD' });
        if (testResponse.ok) {
          setPreviewContent(viewerUrl);
          setPreviewMode(['xls', 'xlsx'].includes(extension) ? 'microsoft' : 'google');
          setLoading(false);
          return;
        }
      } catch (err) {
        console.log('File not publicly accessible, falling back to local processing');
      }
    }

    // Set mode to local processing
    setPreviewMode('local');

    // Fallback to local processing for private files or if Google Viewer fails
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const arrayBuffer = await response.arrayBuffer();

      if (extension === 'docx') {
        await loadWordDocument(arrayBuffer);
      } else if (extension === 'doc') {
        setError('DOC files are not supported. Please use DOCX format for preview.');
      } else if (['xls', 'xlsx'].includes(extension)) {
        await loadExcelDocument(arrayBuffer);
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError('Failed to load file preview. The file might be corrupted or in an unsupported format.');
    } finally {
      setLoading(false);
    }
  };

  const loadWordDocument = async (arrayBuffer) => {
    try {
      // Enhanced Word document conversion with better styling
      const result = await mammoth.convertToHtml({ 
        arrayBuffer,
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Title'] => h1.title:fresh",
          "p[style-name='Subtitle'] => h2.subtitle:fresh",
          "r[style-name='Strong'] => strong",
          "r[style-name='Emphasis'] => em"
        ]
      });
      
      // Enhanced Word document styling
      const wordDocumentStyles = `
        <style>
          .word-document {
            max-width: 8.5in;
            margin: 0 auto;
            padding: 1in;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            font-family: 'Times New Roman', Times, serif;
            font-size: 12pt;
            line-height: 1.15;
            color: #000;
          }
          .word-document h1 {
            font-size: 18pt;
            font-weight: bold;
            margin: 12pt 0;
            color: #1f4788;
          }
          .word-document h2 {
            font-size: 14pt;
            font-weight: bold;
            margin: 10pt 0;
            color: #2e5090;
          }
          .word-document h3 {
            font-size: 12pt;
            font-weight: bold;
            margin: 8pt 0;
            color: #365f91;
          }
          .word-document p {
            margin: 6pt 0;
            text-align: justify;
          }
          .word-document strong {
            font-weight: bold;
          }
          .word-document em {
            font-style: italic;
          }
          .word-document table {
            border-collapse: collapse;
            width: 100%;
            margin: 12pt 0;
          }
          .word-document table, .word-document th, .word-document td {
            border: 1px solid #000;
          }
          .word-document th, .word-document td {
            padding: 6pt;
            text-align: left;
          }
          .word-document th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .word-document ul, .word-document ol {
            margin: 6pt 0;
            padding-left: 24pt;
          }
          .word-document li {
            margin: 3pt 0;
          }
        </style>
      `;
      
      setWordStyles(wordDocumentStyles);
      setPreviewContent(result.value);
      
      if (result.messages.length > 0) {
        console.warn('Word document conversion warnings:', result.messages);
      }
    } catch (err) {
      throw new Error('Failed to convert Word document');
    }
  };

  const loadExcelDocument = async (arrayBuffer) => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetData = [];
      
      workbook.SheetNames.forEach((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        sheetData.push({
          name: sheetName,
          data: jsonData
        });
      });
      
      setSheets(sheetData);
      setActiveSheet(0);
    } catch (err) {
      throw new Error('Failed to convert Excel document');
    }
  };

  const renderExcelSheet = (sheetData) => {
    if (!sheetData || sheetData.length === 0) {
      return <div className="p-4 text-gray-500">No data in this sheet</div>;
    }

    return (
      <div className="excel-sheet-container">
        <style jsx>{`
          .excel-sheet-container {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            background: #fafafa;
          }
          .excel-table {
            border-collapse: separate;
            border-spacing: 0;
            width: 100%;
            background: white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .excel-cell {
            border: 1px solid #d0d7de;
            padding: 4px 8px;
            min-width: 50px;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            background: white;
            position: relative;
          }
          .excel-header-cell {
            background: #f6f8fa;
            font-weight: 600;
            color: #24292f;
            border-bottom: 2px solid #d0d7de;
          }
          .excel-row-number {
            background: #f6f8fa;
            font-weight: 600;
            color: #656d76;
            text-align: center;
            min-width: 40px;
            border-right: 2px solid #d0d7de;
          }
          .excel-column-header {
            background: #f6f8fa;
            font-weight: 600;
            color: #656d76;
            text-align: center;
            border-bottom: 2px solid #d0d7de;
          }
          .excel-cell:hover {
            background: #f0f9ff;
          }
        `}</style>
        
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-cell excel-row-number"></th>
              {sheetData[0] && sheetData[0].map((_, colIndex) => (
                <th key={colIndex} className="excel-cell excel-column-header">
                  {String.fromCharCode(65 + colIndex)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheetData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="excel-cell excel-row-number">{rowIndex + 1}</td>
                {Array.from({ length: Math.max(...sheetData.map(r => r.length)) }).map((_, colIndex) => (
                  <td 
                    key={colIndex} 
                    className={`excel-cell ${rowIndex === 0 ? 'excel-header-cell' : ''}`}
                    title={row[colIndex] || ''}
                  >
                    {row[colIndex] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.open(fileLink, '_blank')}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Download File Instead
          </button>
        </div>
      );
    }

    if (imageFormats.includes(fileType.toLowerCase())) {
      return (
        <img
          src={fileLink}
          alt="Preview"
          className="max-h-full max-w-full object-contain"
        />
      );
    } else if (fileType.toLowerCase() === 'docx') {
      // Check if we're using Google Drive Viewer
      if (previewMode === 'google') {
        return (
          <div className="w-full h-full bg-white relative">
            <iframe
              src={previewContent}
              width="100%"
              height="100%"
              title="Document Preview"
              className="border-0"
              onLoad={() => console.log('Google Drive Viewer loaded successfully')}
              onError={() => {
                console.log('Google Drive Viewer failed, attempting local fallback');
                setError('Google Drive Viewer failed to load. Falling back to basic preview.');
                setPreviewMode('local');
                // Re-trigger local processing
                const fileUrl = fileLink;
                const extension = fileName.split('.').pop().toLowerCase();
                loadFileContent(fileUrl, extension);
              }}
            />
            <div className="absolute top-2 right-2 bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
              High-Fidelity Preview
            </div>
          </div>
        );
      }
      
      // Local mammoth processing fallback
      return (
        <div className="w-full h-full overflow-auto" style={{ background: '#f5f5f5' }}>
          <div className="bg-yellow-100 text-yellow-800 px-3 py-2 text-sm border-b">
            <span className="font-medium">Basic Preview:</span> For better formatting, ensure the file is publicly accessible.
          </div>
          <div dangerouslySetInnerHTML={{ __html: wordStyles }} />
          <div className="word-document">
            <div dangerouslySetInnerHTML={{ __html: previewContent }} />
          </div>
        </div>
      );
    } else if (['xls', 'xlsx'].includes(fileType.toLowerCase())) {
      // Check if we're using Microsoft Office Online Viewer
      if (previewMode === 'microsoft') {
        return (
          <div className="w-full h-full bg-white relative">
            <iframe
              src={previewContent}
              width="100%"
              height="100%"
              title="Excel Preview"
              className="border-0"
              onLoad={() => console.log('Microsoft Office Viewer loaded successfully')}
              onError={() => {
                console.log('Microsoft Office Viewer failed, attempting local fallback');
                setError('Microsoft Office Viewer failed to load. Falling back to basic preview.');
                setPreviewMode('local');
                // Re-trigger local processing
                const fileUrl = fileLink;
                const extension = fileName.split('.').pop().toLowerCase();
                loadFileContent(fileUrl, extension);
              }}
            />
            <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium flex items-center space-x-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 3h20c.55 0 1 .45 1 1v16c0 .55-.45 1-1 1H2c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1zm2 2v14h16V5H4zm2 2h12v2H6V7zm0 4h12v2H6v-2zm0 4h12v2H6v-2z"/>
              </svg>
              <span>Microsoft Excel Online</span>
            </div>
          </div>
        );
      }
      
      // Local xlsx processing fallback
      return (
        <div className="w-full h-full flex flex-col">
          <div className="bg-yellow-100 text-yellow-800 px-3 py-2 text-sm border-b flex items-center space-x-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <span className="font-medium">Basic Preview:</span> For charts, formulas, pivot tables, and full Excel functionality, ensure the file is publicly accessible.
            </div>
          </div>
          {/* Sheet Tabs */}
          {sheets.length > 1 && (
            <div className="flex border-b bg-gray-50 overflow-x-auto">
              {sheets.map((sheet, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSheet(index)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-r border-gray-200 transition-colors ${
                    activeSheet === index
                      ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {sheet.name}
                </button>
              ))}
            </div>
          )}
          
          {/* Sheet Content */}
          <div className="flex-1 overflow-auto">
            {sheets[activeSheet] && renderExcelSheet(sheets[activeSheet].data)}
          </div>
        </div>
      );
    } else {
      // Fallback for PDFs and other supported formats
      return (
        <iframe
          src={fileLink}
          width="100%"
          height="100%"
          title="Document Preview"
          onError={() => setError('Unable to preview this file type')}
        />
      );
    }
  };

  return (
    <div
      id="preview-file-modal"
      className={`${
        openModal ? 'flex' : 'hidden'
      } fixed inset-0 z-50 items-center justify-center bg-black bg-opacity-50 overflow-y-auto overflow-x-hidden`}
      aria-hidden={!openModal}
    >
      <div className="relative w-full max-w-full max-h-full p-4">
        <div className="bg-white rounded-lg shadow-2xl dark:bg-gray-700 max-w-7xl mx-auto">
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {fileType.toLowerCase() === 'docx' && (
                  <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">W</span>
                  </div>
                )}
                {['xls', 'xlsx'].includes(fileType.toLowerCase()) && (
                  <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">X</span>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {fileName}
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400 uppercase font-medium">
                  {fileType} file
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => window.open(fileLink, '_blank')}
                className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Download
              </button>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={() => setOpenModal(false)}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
          </div>
          <div className="h-[85vh] overflow-hidden">
            {renderPreview()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewFileModal;